import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { AiDocumentationFieldEnum } from 'graphql-common';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Typography from '@lib/components/Typography/Typography';
import { useSharedAiBudget } from 'components/AiAssistant/AiBudgetContext';
import AiCreditsIndicator, {
  AiCredits,
} from 'components/AiAssistant/AiCreditsIndicator';
import { getAiBudgetBlockedText } from 'components/AiAssistant/utils/getAiBudgetBlock';
import { useSharedAiFieldGeneration } from 'routes/Documentation/components/AiDetection/AiFieldGenerationContext';
import resolveAiFormSource from '../../utils/resolveAiFormSource';
import styles from './AiAutofillRow.module.scss';

type Props = {
  isEdit?: boolean;
  documentationVersionId?: string;
  credits?: AiCredits | null;
};

export const AI_AUTOFILL_FIELDS = [
  AiDocumentationFieldEnum.Name,
  AiDocumentationFieldEnum.Description,
  AiDocumentationFieldEnum.Category,
  AiDocumentationFieldEnum.Code,
  AiDocumentationFieldEnum.Tags,
];

export default function AiAutofillRow({
  isEdit,
  documentationVersionId,
  credits,
}: Props) {
  const { t } = useTranslation();
  const { getValues } = useFormContext<Record<string, unknown>>();
  // Autofilling every field at once is the most expensive AI action on the
  // form, so it is the first thing a refused budget has to take away.
  const aiBudget = useSharedAiBudget();
  const aiBlockedText = getAiBudgetBlockedText(t, aiBudget?.blockedReason);
  const aiGeneration = useSharedAiFieldGeneration();
  const [batchId, setBatchId] = useState<string | null>(null);
  const successfulBatchIdRef = useRef<string | null>(null);
  const [hasSuccessfulGeneration, setHasSuccessfulGeneration] = useState(false);

  if (!aiGeneration) return null;

  const { generateAll, getFieldState } = aiGeneration;
  const fieldStates = AI_AUTOFILL_FIELDS.map((field) => getFieldState(field));

  const isBatchRunning = fieldStates.some(
    (state) => state.generationId === batchId && state.status === 'generating',
  );

  const isCurrentBatchSuccessful = fieldStates.some(
    (state) => state.generationId === batchId && state.status === 'done',
  );

  if (
    batchId &&
    isCurrentBatchSuccessful &&
    successfulBatchIdRef.current !== batchId
  ) {
    successfulBatchIdRef.current = batchId;
    setHasSuccessfulGeneration(true);
  }

  const onClick = () => {
    if (aiBlockedText) return;
    const source = resolveAiFormSource({
      getValues,
      isEdit,
      documentationVersionId,
    });
    if (!source) return;
    const generationId = generateAll({ fields: AI_AUTOFILL_FIELDS, source });
    setBatchId(generationId);
  };

  return (
    <div className={styles.root}>
      <Button
        buttonText={
          hasSuccessfulGeneration
            ? t('ai-regenerate')
            : t('ai-autofill-with-ai')
        }
        buttonType={ButtonTypes.primaryOutlined}
        leftIcon="auto_awesome"
        onClick={onClick}
        disabled={isBatchRunning || !!aiBlockedText}
        isLoading={isBatchRunning}
      />
      {/* The block reason takes the credits line's place: with the button
          disabled, "900 / 1000 remaining" would only confuse. */}
      {aiBlockedText ? (
        <Typography variant="label" block>
          {aiBlockedText}
        </Typography>
      ) : (
        <AiCreditsIndicator credits={credits} />
      )}
    </div>
  );
}
